import type { ReactNode } from 'react';
import { cn } from '../cn.js';

export type BadgeTone =
  | 'neutral'
  | 'brand'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'violet'
  | 'teal'
  | 'purple'
  | 'sky'
  | 'amber'
  | 'green';

const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-neutral-bg text-neutral-fg',
  brand: 'bg-brand-subtle text-brand-text',
  success: 'bg-success-bg text-success-fg',
  warning: 'bg-warning-bg text-warning-fg',
  danger: 'bg-danger-bg text-danger-fg',
  info: 'bg-info-bg text-info-fg',
  violet: 'bg-accent-violet-bg text-accent-violet-fg',
  teal: 'bg-accent-teal-bg text-accent-teal-fg',
  purple: 'bg-accent-purple-bg text-accent-purple-fg',
  sky: 'bg-accent-sky-bg text-accent-sky-fg',
  amber: 'bg-accent-amber-bg text-accent-amber-fg',
  green: 'bg-accent-green-bg text-accent-green-fg',
};

export interface BadgeProps {
  tone?: BadgeTone;
  children: ReactNode;
  icon?: ReactNode;
  className?: string;
  title?: string;
}

export function Badge({ tone = 'neutral', children, icon, className, title }: BadgeProps) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center rounded-full font-semibold px-2 py-0.5 text-xs gap-1',
        TONES[tone],
        className,
      )}
    >
      {icon}
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Domain-specific badges. Keeping the status-to-colour mapping in one place
// stops "failed" from being red on one screen and amber on another, which
// matters when a screenshot becomes audit evidence.
// ---------------------------------------------------------------------------

const JOB_STATUS_TONE: Record<string, BadgeTone> = {
  'pending-approval': 'warning',
  approved: 'info',
  queued: 'neutral',
  running: 'sky',
  succeeded: 'success',
  failed: 'danger',
  cancelled: 'neutral',
  rejected: 'danger',
  expired: 'neutral',
};

const JOB_STATUS_LABEL: Record<string, string> = {
  'pending-approval': 'Pending approval',
  approved: 'Approved',
  queued: 'Queued',
  running: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
  expired: 'Expired',
};

export function JobStatusBadge({ status }: { status: string }) {
  return (
    <Badge tone={JOB_STATUS_TONE[status] ?? 'neutral'}>{JOB_STATUS_LABEL[status] ?? status}</Badge>
  );
}

const ENVIRONMENT_TONE: Record<string, BadgeTone> = {
  dev: 'neutral',
  test: 'teal',
  staging: 'amber',
  // Production is the one an operator must never misread at a glance.
  prod: 'danger',
};

export function EnvironmentBadge({ environment }: { environment: string }) {
  return (
    <Badge tone={ENVIRONMENT_TONE[environment] ?? 'neutral'} className="uppercase tracking-wide">
      {environment}
    </Badge>
  );
}

const RISK_TONE: Record<string, BadgeTone> = {
  low: 'neutral',
  medium: 'info',
  high: 'warning',
  critical: 'danger',
};

export function RiskBadge({ risk }: { risk: string }) {
  return <Badge tone={RISK_TONE[risk] ?? 'neutral'}>{risk}</Badge>;
}
